import {Card, Container, Group, Menu, rem, Space, Tabs, Text, Title, UnstyledButton} from "@mantine/core";
import {useLocation, useNavigate} from "react-router-dom";
import {PropsWithChildren, useCallback, useMemo} from "react";
import {
  lastOrUndefined,
  PipelineEntity,
  pipelineGraphsSelector,
  useGetPipelineGraphsByPipelineQuery
} from "@ots-frontend/ots-data";
import {PipelineDetails} from "./PipelineDetails";
import {createContentStyles} from "../../../theme/useContentStyles";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {PipelineAction, PipelineActionMenuDropdown} from "../../menu/PipelineActionMenu/PipelineActionMenu";
import {usePipelineUpdate} from "../../../hooks/usePipelineUpdate";
import {ObjectChangeLogView} from "../common/ObjectChangeLogView/ObjectChangeLogView";
import {PipelineInsights} from "./PipelineInsights";

const useStyles = createContentStyles((theme) => ({
  mainSection: {
    paddingBottom: theme.spacing.sm
  }
}));

export type PipelineProfileProps = PropsWithChildren<{
  pipeline: PipelineEntity
}>

const tabs = [
  {
    key: "details",
    value: "Details"
  },
  {
    key: "insights",
    value: "Insights"
  },
  {
    key: 'history',
    value: 'Change Log'
  }
];

export const PipelineProfile = (props: PipelineProfileProps) => {
  const {pipeline} = props;

  const {classes} = useStyles();

  const navigate = useNavigate();
  const {state} = useLocation();
  const {t} = useTranslation()

  const {
    deletePipeline
  } = usePipelineUpdate()

  const selectPipelineGraphs = useMemo(() => {
    return pipelineGraphsSelector();
  }, []);

  const {pipelineGraphs, isPipelineGraphsLoading} = useGetPipelineGraphsByPipelineQuery(pipeline.id, {
    selectFromResult: (result) => ({
      pipelineGraphs: selectPipelineGraphs(result, {pipelineId: pipeline.id}),
      isPipelineGraphsLoading: result.isLoading
    })
  });

  const onNewPipelineGraphClick = useCallback(() => {
    const sortedGraphs = pipelineGraphs?.sort((a, b) => a.effectiveDttm.valueOf() - b.effectiveDttm.valueOf());

    navigate("graph-builder", {
      state: {
        pipelineId: pipeline.id,
        pipelineGraphId: lastOrUndefined(sortedGraphs)?.id,
        newVersion: true,
      }
    });
  }, [navigate, pipeline, pipelineGraphs])

  const onActionClick = useCallback((action: PipelineAction) => {
    if (action === 'EDIT') {
      navigate(`/pipelines/form`, {state: {id: pipeline.id}})
    } else if (action === 'DELETE') {
      deletePipeline(pipeline.id, () => {
        navigate(-1)
      })
    } else if (action === 'NEW_PIPELINE_GRAPH') {
      onNewPipelineGraphClick()
    }
  }, [pipeline, navigate, deletePipeline, onNewPipelineGraphClick])

  const items = tabs.map((tab) => (
    <Tabs.Tab value={tab.key} key={tab.key}>
      {tab.value}
    </Tabs.Tab>
  ));

  return (
    <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
      <Container fluid={true} className={classes.mainSection}>
        <Group position="apart">
          <Title order={4}>Pipeline: {pipeline.name}</Title>
          {!isPipelineGraphsLoading &&
            <Menu
              width={260}
              position="bottom-end"
              transitionProps={{transition: "pop-top-right"}}
              withinPortal
            >
              <Menu.Target>
                <UnstyledButton>
                  <Group spacing={7}>
                    <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                      {t("common.action.plural", "Actions")}
                    </Text>
                    <IconChevronDown size={rem(12)} stroke={1.5}/>
                  </Group>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <PipelineActionMenuDropdown pipeline={pipeline} onActionClick={onActionClick}/>
              </Menu.Dropdown>
            </Menu>
          }
        </Group>
      </Container>

      <Card.Section>
        <Container fluid={true}>
          <Tabs
            defaultValue={state?.tabValue ?? "details"}
            onTabChange={(value) => navigate(`?id=${pipeline.id}`, {state: {tabValue: value}, replace: true})}
          >
            <Tabs.List>{items}</Tabs.List>
            <Space h="md"/>
            <Tabs.Panel value="details">
              <PipelineDetails pipeline={pipeline}/>
            </Tabs.Panel>
            <Tabs.Panel value="insights">
              <PipelineInsights pipeline={pipeline}/>
            </Tabs.Panel>
            <Tabs.Panel value="history">
              <ObjectChangeLogView entity={pipeline}/>
            </Tabs.Panel>
          </Tabs>
        </Container>
      </Card.Section>
    </Card>
  );
};
